<script setup lang="ts">
//#region imports
import { format, startOfDay } from 'date-fns';
import { cloneDeepWith } from 'lodash-es';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { isErrorResponse, type EmitsToListeners, type FormErrors } from '@/types/core';
import type { OverrideFields } from '@/types/util';
import type { PostAdminsData } from '@/types/openapi-ts';
//#endregion

//#region types
type EditableDate = Date | string | null;

type FormType = OverrideFields<PostAdminsData['body'], {
  entities_visibility_from: EditableDate;
  entities_visibility_to: EditableDate;
}>;

interface Emits {
  success: [];
  close: [];
}

export interface CreateTeamUserParams {
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const emit = defineEmits<Emits>();
defineOptions({
  name: 'CreateTeamUser',
});
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const isOverall = ref(false);
const form = ref<FormType>(createDefaultForm());
const errors = ref<FormErrors>({});
//#endregion

//#region computed
const timezones = computed(() => store.getters['misc/timezones']);
const messengers = computed(() => store.getters['misc/apiMessengers']);
const messengerPlaceholder = computed(() => {
  if (!form.value.messenger) {
    return t('team_popup.messenger_id_placeholder');
  }

  return messengers.value.find(
    (messenger) => messenger.id === form.value.messenger,
  )?.example_placeholder ?? '';
});
//#endregion

//#region methods
function createDefaultForm(): FormType {
  return {
    name: '',
    email: '',
    timezone: 'UTC',
    messenger: '',
    messenger_id: '',
    notes: '',
    entities_visibility_from: '',
    entities_visibility_to: '',
  };
}

function open() {
  form.value = createDefaultForm();
  isOverall.value = false;
  errors.value = {};
  isVisible.value = true;
}

function handleClose() {
  isVisible.value = false;
  errors.value = {};
  emit('close');
}

function setErrors(error: unknown) {
  if (!isErrorResponse(error)) {
    throw error;
  }

  errors.value = cloneDeepWith(error.data.errors) ?? {};
}

function formatDate(value: EditableDate) {
  if (value === null || value === '') {
    return null;
  }

  return format(startOfDay(value), 'yyyy-MM-dd HH:mm:ss');
}

function handleSave() {
  const data: PostAdminsData['body'] = {
    ...form.value,
    entities_visibility_from: isOverall.value
      ? null
      : formatDate(form.value.entities_visibility_from),
    entities_visibility_to: isOverall.value
      ? null
      : formatDate(form.value.entities_visibility_to),
  };

  api.postAdmins(data).then(() => {
    emit('success');
    handleClose();
  }).catch(setErrors);
}
//#endregion

defineExpose({ open, handleClose });
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="t('team_popup.create_user_title')"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="form.name"
          :error="errors.name"
          :placeholder="t('team_popup.name_placeholder')"
          :label="t('team_popup.name_label')"
          size="big"
          autosize
        />
        <UiInput
          v-model="form.email"
          :error="errors.email"
          :placeholder="t('team_popup.email_placeholder')"
          :label="t('team_popup.email_label')"
          size="big"
          autosize
        />
        <UiSelectElement
          v-model="form.timezone"
          :placeholder="t('team_popup.timezone_placeholder')"
          :label="t('team_popup.timezone_label')"
          :list="timezones"
          option-value-param="zone"
          filterable
          size="big"
        />
        <div class="t:space-y-8">
          <div class="t:flex t:items-center">
            <UiLabel :label="t('team_popup.see_stats_label')" />
            <UiCheckbox
              v-model="isOverall"
              :label="t('team_popup.see_stats_overall')"
              class="t:ml-8"
            />
          </div>
          <div class="t:flex t:items-center">
            <UiDatePicker
              v-model="form.entities_visibility_from"
              class="t:w-full"
              :disabled="isOverall"
              :placeholder="t('team_popup.see_stats_placeholder')"
              :multi-calendars="false"
              :range="false"
              teleport
              auto-apply
              :always-clearable="false"
              :is-error="!!errors.entities_visibility_from"
              size="big"
            />
            <div class="t:mx-3">
              –
            </div>
            <UiDatePicker
              v-model="form.entities_visibility_to"
              class="t:w-full"
              :disabled="isOverall"
              :placeholder="t('team_popup.see_stats_placeholder')"
              :multi-calendars="false"
              :range="false"
              teleport
              auto-apply
              :always-clearable="false"
              :is-error="!!errors.entities_visibility_to"
              size="big"
            />
          </div>
          <UiHelp
            v-if="errors.entities_visibility_to?.[0]"
            :text="t(errors.entities_visibility_to[0].message)"
            is-error
          />
        </div>
        <div class="t:flex t:items-start t:space-x-16">
          <UiSelectElement
            v-model="form.messenger"
            :placeholder="t('team_popup.messenger_placeholder')"
            :label="t('team_popup.messenger_label')"
            :list="messengers"
            size="big"
          />
          <UiInput
            v-model="form.messenger_id"
            :error="errors.messenger_id"
            :placeholder="messengerPlaceholder"
            :disabled="!form.messenger"
            :label="t('team_popup.messenger_id_label')"
            size="big"
            autosize
          />
        </div>
        <UiTextarea
          v-model="form.notes"
          :label="t('team_popup.note_label')"
          :placeholder="t('team_popup.note_placeholder')"
          :rows="3"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ t('team_popup.cancel_button') }}
        </UiButton>
        <UiButton
          filled
          type="positive"
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ t('team_popup.create_button') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
